"use client";

import { withParentSize } from "@visx/responsive";
import { scaleTime } from "@visx/scale";
import { Line } from "@visx/shape";
import React, { isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  ChartContext,
  defaultAnimationSettings,
  type TooltipData,
  useChartEntrance,
  type YFormatType,
} from "../../../context/ChartContext";
import { useChartExport } from "../../../hooks/useChartExport";
import { useColorMode } from "../../../utils/ColorModeProvider";
import {
  type BaseDataPoint,
  createYScale,
  defaultMargin,
  getYFormatSettings,
  resolveAxisFormatter,
} from "../../../utils/charts";
import { getDefaultChartColor, getThemeCategoricalColors } from "../../../utils/colors";
import { findNearestTooltipData } from "../../../utils/findNearestTooltipData";
import { getMaxTextWidth } from "../../../utils/measureText";
import { AreaSeries, type AreaSeriesBaseline } from "../AreaSeries";
import { ChartAxis, calculateOptimalYTickCount } from "../ChartAxis";
import { LiveTimeAxisBottom } from "../ChartAxis/LiveTimeAxisBottom";
import { LiveTimeGridlines } from "../ChartAxis/LiveTimeGridlines";
import type { LiveTimePreviewHandle } from "../ChartAxis/useLiveTimeTicks";
import { ChartBottomBar } from "../ChartBottomBar";
import { useTimeCameraGesture } from "../ChartContainer/useTimeCameraGesture";
import { ChartHeader, useChartHeaderMetrics, useOccupiedHeight } from "../ChartHeader";
import { useChartExpand } from "../ChartHeader/useChartExpand";
import { ChartTooltip } from "../ChartTooltip";
import { flattenChartChildren } from "../flattenChartChildren";
import { LineSeries } from "../LineSeries";
import type { TiledCameraPreviewDomain, TiledCameraSyncGroup } from "./cameraSyncGroup";
import { SelectionBand, type SelectionBandHandle } from "./SelectionBand";

type SeriesProps = React.ComponentProps<typeof AreaSeries> | React.ComponentProps<typeof LineSeries>;

const isSeriesElement = (child: React.ReactElement<unknown>): child is React.ReactElement<SeriesProps> =>
  child.type === AreaSeries || child.type === LineSeries;

export interface TiledTimeSeriesChartViewport {
  startMs: number;
  endMs: number;
}

export interface TiledTimeSeriesChartProps {
  /** Drop the Export control while keeping the legend. See `ChartBottomBarProps.hideExport`. */
  hideExport?: boolean;
  /**
   * Series labels the legend shows struck through and the plot omits. Controlled only — the
   * caller owns the set, because the thing a reader toggles is usually a group of series (every
   * signal on one phase) rather than the one label they clicked.
   */
  hiddenSeriesLabels?: ReadonlySet<string>;
  /** Makes the legend clickable. Receives the pressed item's `id`, or its label when it has none. */
  onLegendItemToggle?: (label: string) => void;
  /**
   * Legend entries to show INSTEAD of one per series.
   *
   * For a chart whose series are a cross-product the reader does not think in: three phases × three
   * signals is nine lines, but the questions are "which phases?" and "which signals?" — six
   * entries, not nine. The caller maps an entry back to the series it governs and answers with
   * `hiddenSeriesLabels`, so this prop is presentation only; it never decides what is drawn.
   */
  legend?: { category: string; fill: string; isHidden?: boolean; id?: string }[];
  /**
   * Moments worth flagging on the time axis.
   *
   * `point` is the reading that caused the mark. Hovering shows that one value, not the series the
   * plot happens to be drawing — the mark exists because of a single sample, and answering with
   * five other numbers makes the reader hunt for the one that crossed. It is shown even when that
   * series is hidden, since the mark is the only trace of it left on screen.
   */
  xMarkers?: { timeMs: number; label: string; color?: string; point?: { label: string; value: number } }[];
  /** AreaSeries / LineSeries children. */
  children: React.ReactNode;
  /**
   * Accessible name for the chart, rendered as the SVG `<title>`. Required so
   * that screen readers announce what each chart shows; every chart needs its
   * own, and there is deliberately no default to fall back to.
   */
  title: string;
  /** Trailing actions rendered in the `ChartHeader`, opposite the title. */
  headerActions?: React.ReactNode;
  /** Control cluster rendered in the `ChartHeader` — `TimeControls` today. */
  headerControls?: React.ReactNode;
  /** The camera. Drives the x-scale directly, decoupled from the data extent. */
  viewport: TiledTimeSeriesChartViewport;
  /** Optional clamp for pan gestures. New viewports stay within these bounds. */
  panBounds?: TiledTimeSeriesChartViewport;
  /** Resolve current pan bounds at gesture commit without updating the plotted viewport. */
  getPanBounds?: (() => TiledTimeSeriesChartViewport) | undefined;
  /** Committed once per settled gesture (drag/wheel), with the new ms viewport. */
  onViewportChange: (viewport: TiledTimeSeriesChartViewport) => void;
  /** Measured PLOT width (after margins), for level-of-detail / tile resolution. */
  onPlotWidthChange?: (widthPx: number) => void;
  yFormatter?: YFormatType;
  /** Explicit bounds or a domain derived from visible geometry, including connected bracketing samples. */
  yDomain?: [number, number] | ((data: BaseDataPoint[]) => [number, number]);
  /** Horizontal reference lines (regulatory limits, thresholds). */
  referenceLines?: { value: number; label?: string; color?: string }[];
  /**
   * Shared camera group (see `createTiledCameraSyncGroup`). While a gesture is
   * in progress on any chart in the group, the others mirror its live camera
   * frame-by-frame; the settled commit still arrives via `onViewportChange`.
   */
  cameraSync?: TiledCameraSyncGroup;
  parentWidth?: number;
  parentHeight?: number;
}

const NO_HIDDEN_IDS: ReadonlySet<string> = new Set();

/** A legend identity per drawn series: its label, suffixed when an earlier series already used it. */
function seriesIdentities(children: React.ReactNode): { id: string; label: string }[] {
  const uses = new Map<string, number>();
  return flattenChartChildren(children)
    .filter((child): child is React.ReactElement<SeriesProps> => isValidElement(child) && isSeriesElement(child))
    .filter((child) => child.props.data)
    .map((child, index) => {
      const label = ("label" in child.props && child.props.label) || `Series ${index + 1}`;
      const use = uses.get(label) ?? 0;
      uses.set(label, use + 1);
      return { id: use === 0 ? label : `${label}#${use}`, label };
    });
}

function inLegendOrder<T extends { legendOrder: number | undefined }>(metadata: T[]): T[] {
  return metadata
    .map((meta, index) => ({ meta, rank: meta.legendOrder ?? Number.POSITIVE_INFINITY, index }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ meta }) => meta);
}

const VisXTiledTimeSeriesChart: React.FC<TiledTimeSeriesChartProps> = ({
  children,
  title,
  hideExport = false,
  hiddenSeriesLabels,
  onLegendItemToggle,
  legend,
  xMarkers,
  headerActions,
  headerControls,
  viewport,
  panBounds,
  getPanBounds,
  onViewportChange,
  onPlotWidthChange,
  yFormatter = { type: "number" },
  yDomain,
  referenceLines,
  cameraSync,
  parentWidth = 0,
  parentHeight = 0,
}) => {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastMoveRef = useRef<number>(0);
  const plotClipId = useId();
  const plotGroupRef = useRef<SVGGElement>(null);
  const axisRef = useRef<LiveTimePreviewHandle>(null);
  const gridRef = useRef<LiveTimePreviewHandle>(null);

  const { colorMode } = useColorMode();
  const themeColors = useMemo(() => getThemeCategoricalColors(), [colorMode]);
  const defaultColor = useMemo(() => getDefaultChartColor(), [colorMode]);

  const ownsLegend = hiddenSeriesLabels === undefined && onLegendItemToggle === undefined && legend === undefined;
  const [ownHiddenIds, setOwnHiddenIds] = useState<ReadonlySet<string>>(() => new Set());
  const identities = useMemo(() => seriesIdentities(children), [children]);
  const ownHiddenShown = useMemo(() => {
    const present = identities.filter(({ id }) => ownHiddenIds.has(id));
    return present.length === 0 || present.length >= identities.length
      ? NO_HIDDEN_IDS
      : new Set(present.map(({ id }) => id));
  }, [identities, ownHiddenIds]);
  const isSeriesHidden = useCallback(
    (meta: { id: string; label: string }) =>
      ownsLegend ? ownHiddenShown.has(meta.id) : (hiddenSeriesLabels?.has(meta.label) ?? false),
    [ownsLegend, ownHiddenShown, hiddenSeriesLabels]
  );
  const toggleOwnLegendItem = useCallback((id: string) => {
    setOwnHiddenIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const { datasets, seriesMetadata, seriesTypes } = useMemo(() => {
    const seriesChildren = flattenChartChildren(children).filter(
      (child): child is React.ReactElement<SeriesProps> => isValidElement(child) && isSeriesElement(child)
    );

    const totalSeries = seriesChildren.filter((child) => child.props.data).length;
    const data: BaseDataPoint[][] = [];
    const metadata: Array<{
      id: string;
      label: string;
      color: string;
      legendOrder: number | undefined;
      baseline?: AreaSeriesBaseline;
      tooltipRange?: boolean;
      maxGapMs: number | undefined;
    }> = [];
    const types: Array<"area" | "line" | "bar" | "point"> = [];

    seriesChildren.forEach((child, seriesIndex) => {
      const childProps = child.props as SeriesProps;
      if (!childProps.data) return;

      // Hidden series keep their slot — dropping them would reindex colours and renumber the
      // legend — but contribute no points, so they leave the y-domain and the tooltip alone.
      const { id, label } = identities[data.length] ?? { id: `Series ${data.length + 1}`, label: "" };
      data.push(isSeriesHidden({ id, label }) ? [] : (childProps.data as BaseDataPoint[]));

      const color = "color" in childProps && childProps.color ? childProps.color : undefined;
      const resolvedColor = color || (totalSeries === 1 ? defaultColor : themeColors[seriesIndex % themeColors.length]);

      const isArea = child.type === AreaSeries;
      metadata.push({
        id,
        label,
        color: resolvedColor,
        legendOrder: childProps.legendOrder,
        maxGapMs: childProps.maxGapMs,
        ...(isArea && "baseline" in childProps && childProps.baseline !== undefined
          ? {
              baseline: childProps.baseline,
              tooltipRange: ("tooltipRange" in childProps && childProps.tooltipRange) || false,
            }
          : {}),
      });
      types.push(isArea ? "area" : "line");
    });

    return { datasets: data, seriesMetadata: metadata, seriesTypes: types };
  }, [children, identities, defaultColor, themeColors, isSeriesHidden]);

  const expand = useChartExpand(title);
  // Expanded, the HOC's container stays behind in the layout and keeps
  // reporting the inline size; the body is measured where it actually is.
  const width = expand.expandedSize?.width ?? parentWidth;
  const resolvedActions =
    headerActions || expand.action ? (
      <>
        {headerActions}
        {expand.action}
      </>
    ) : undefined;
  const { headerRef, headerHeight } = useChartHeaderMetrics(Boolean(resolvedActions), Boolean(headerControls));
  // Measured, not assumed: the legend wraps to a second row once a chart draws nine series, and a
  // constant reserve clipped that row against the plot box's fixed height. 50 is the one-row
  // estimate used for the first paint only (bar 32 + its 8px gap + ~10 beneath it).
  const { ref: bottomBarRef, height: measuredBottomBarHeight } = useOccupiedHeight();
  const height = Math.max(
    100,
    (expand.expandedSize?.height ?? parentHeight) - headerHeight - (measuredBottomBarHeight || 50)
  );

  const scaleDomain = useMemo<[Date, Date]>(
    () => [new Date(viewport.startMs), new Date(viewport.endMs)],
    [viewport.startMs, viewport.endMs]
  );

  // The y-domain follows what's on screen: loaded data extends past the
  // viewport (overscan tiles), and letting offscreen buckets set the scale
  // leaves the visible line dwarfed under an inflated axis. When the viewport
  // holds no points (panning through a gap), fall back to the full loaded set
  // rather than collapsing to a degenerate domain.
  const yScaleData = useMemo(() => {
    const all = datasets.flat();
    const visible = datasets.flatMap((data, seriesIndex) =>
      data.filter((point, index) => {
        const t = point.xValue.getTime();
        if (t >= viewport.startMs && t <= viewport.endMs) return true;
        if (typeof yDomain !== "function") return false;

        // A connected offscreen endpoint still contributes geometry inside the plot clip.
        const neighbor = t < viewport.startMs ? data[index + 1] : data[index - 1];
        if (!neighbor) return false;
        const neighborTime = neighbor.xValue.getTime();
        const maxGapMs = seriesMetadata[seriesIndex].maxGapMs;
        if (maxGapMs != null && maxGapMs > 0 && Math.abs(neighborTime - t) > maxGapMs) return false;
        return t < viewport.startMs ? neighborTime > viewport.startMs : neighborTime < viewport.endMs;
      })
    );
    return visible.length > 0 ? visible : all;
  }, [datasets, seriesMetadata, viewport.startMs, viewport.endMs, yDomain]);
  const yScale = useMemo(() => {
    const resolvedDomain = typeof yDomain === "function" ? yDomain(yScaleData) : yDomain;
    const scale = createYScale(yScaleData, height, yFormatter, resolvedDomain);
    // Auto domains get 5% headroom so a tick landing exactly on the domain max
    // isn't drawn at the plot's top edge with its label clipped. Explicit
    // yDomain is the caller's contract — left untouched.
    if (!yDomain) {
      const [d0, d1] = scale.domain() as [number, number];
      if (d1 > d0) scale.domain([d0, d1 + (d1 - d0) * 0.05]);
    }
    return scale;
  }, [yScaleData, height, yFormatter, yDomain]);

  // Track the last yFormatter serial key so we can detect a deliberate reset
  // vs. a domain change caused by pan/zoom.
  // We compare by value (JSON) rather than object identity so callers who
  // write `yFormatter={{ type: "energy" }}` inline don't accidentally reset
  // the pin on every render — only a genuine change in formatter semantics
  // (type, unit, currency, etc.) starts the pin over.
  const yFormatterKeyRef = useRef<string>(JSON.stringify(yFormatter));
  // The pinned unit chosen on first resolution (or after a reset). Stored as
  // a YFormatType so we carry the whole resolved formatter, not just the unit
  // string, allowing us to spread it back in the memo below.
  const pinnedFormatterRef = useRef<YFormatType | null>(null);

  // One unit for the whole axis (and tooltip), pinned on first resolution and
  // held constant across pan/zoom domain changes. A genuine change in the
  // formatter config (detected by JSON comparison) clears the pin so a fresh
  // unit is chosen from the current domain max.
  //
  // This prevents the Wh → kWh → MWh → GWh flicker that customers see when
  // panning or zooming into a new time window.
  const axisFormatter = useMemo(() => {
    const [d0, d1] = yScale.domain() as [number, number];
    const maxAbsValue = Math.max(Math.abs(d0), Math.abs(d1));

    // Detect a caller-supplied formatter change by comparing serialized values.
    const currentKey = JSON.stringify(yFormatter);
    if (currentKey !== yFormatterKeyRef.current) {
      yFormatterKeyRef.current = currentKey;
      pinnedFormatterRef.current = null;
    }

    // If we already have a pinned formatter (same yFormatter identity), reuse
    // it — do NOT re-resolve from the current domain max.
    if (pinnedFormatterRef.current !== null) {
      return pinnedFormatterRef.current;
    }

    // Defer pinning when the domain is degenerate (no data yet / all zeros).
    // Pinning on an empty domain would lock in the smallest unit (e.g. Wh)
    // before real telemetry arrives, and the pin would never update once set.
    if (maxAbsValue === 0) {
      return resolveAxisFormatter(yFormatter, 0);
    }

    // First resolution for this yFormatter identity: pick and pin the unit.
    const resolved = resolveAxisFormatter(yFormatter, maxAbsValue);
    pinnedFormatterRef.current = resolved;
    return resolved;
  }, [yScale, yFormatter]);

  const yTickCount = useMemo(() => {
    const estimatedInnerHeight = height - defaultMargin.top - defaultMargin.bottom;
    return calculateOptimalYTickCount(estimatedInnerHeight);
  }, [height]);

  const dynamicMargin = useMemo(() => {
    const formatSettings = getYFormatSettings(axisFormatter);
    const tickFormat = formatSettings.tickFormat || formatSettings.format;
    const tickValues = yScale.ticks(yTickCount);
    const formattedTicks = tickValues.map((value) => tickFormat(value));
    const maxLabelWidth = getMaxTextWidth(formattedTicks, 12, "system-ui");
    const calculatedLeftMargin = Math.ceil(maxLabelWidth) + 12;
    return {
      ...defaultMargin,
      left: Math.max(calculatedLeftMargin, 30),
      // 30, not the shared 50: this engine's date labels are one horizontal line that measures 22px,
      // so the rest was empty plot between the axis and the legend under it.
      bottom: 30,
    };
  }, [yScale, axisFormatter, yTickCount]);

  const xScale = useMemo(
    () =>
      scaleTime({
        domain: scaleDomain,
        range: [dynamicMargin.left, width - dynamicMargin.right],
      }),
    [scaleDomain, width, dynamicMargin]
  );

  const cameraXDomainMs = useMemo<[number, number]>(
    () => [viewport.startMs, viewport.endMs],
    [viewport.startMs, viewport.endMs]
  );
  const cameraPanBoundsMs = useMemo<[number, number] | undefined>(
    () => (panBounds ? [panBounds.startMs, panBounds.endMs] : undefined),
    [panBounds]
  );
  const handleCameraCommit = useCallback(
    (domain: [number, number]) => {
      onViewportChange({ startMs: domain[0], endMs: domain[1] });
    },
    [onViewportChange]
  );
  const cameraPlotWidthPx = width - dynamicMargin.left - dynamicMargin.right;
  const handleGestureStart = useCallback(() => setTooltipData(null), []);
  const syncSourceRef = useRef<object>({});
  const cameraSyncRef = useRef<TiledCameraSyncGroup | undefined>(cameraSync);
  cameraSyncRef.current = cameraSync;
  const handleFramePreview = useCallback((domain: [number, number] | null) => {
    axisRef.current?.setPreview(domain);
    gridRef.current?.setPreview(domain);
    cameraSyncRef.current?.publish(syncSourceRef.current, domain);
  }, []);
  const markerOverlayRef = useRef<SVGGElement>(null);
  const selectionRef = useRef<SelectionBandHandle>(null);
  const handleSelectPreview = useCallback((range: [number, number] | null) => {
    selectionRef.current?.setRange(range);
  }, []);
  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, wheelTargetRef, gestureActiveRef } =
    useTimeCameraGesture({
      onSelectPreview: handleSelectPreview,
      overlayRef: markerOverlayRef,
      xDomain: cameraXDomainMs,
      plotWidthPx: cameraPlotWidthPx,
      plotLeftPx: dynamicMargin.left,
      groupRef: plotGroupRef,
      onCommit: handleCameraCommit,
      onGestureStart: handleGestureStart,
      onFramePreview: handleFramePreview,
      ...(cameraPanBoundsMs ? { panBoundsMs: cameraPanBoundsMs } : {}),
      ...(getPanBounds
        ? {
            getPanBoundsMs: (): [number, number] => {
              const bounds = getPanBounds();
              return [bounds.startMs, bounds.endMs];
            },
          }
        : {}),
    });

  // Mirror a sibling chart's live gesture: map this chart's committed domain
  // onto the previewed one with the same translate/scale the gesture owner
  // applies, plus the axis/gridline re-tick — all imperative, so following a
  // 60fps drag never re-renders this component. Geometry rides a ref because
  // the subscription must outlive re-renders without re-subscribing.
  const followerGeomRef = useRef({ startMs: viewport.startMs, endMs: viewport.endMs, left: 0, width: 0 });
  followerGeomRef.current = {
    startMs: viewport.startMs,
    endMs: viewport.endMs,
    left: dynamicMargin.left,
    width: cameraPlotWidthPx,
  };
  useEffect(() => {
    if (!cameraSync) return;
    const clearRemotePreview = () => {
      plotGroupRef.current?.removeAttribute("transform");
      axisRef.current?.setPreview(null);
      gridRef.current?.setPreview(null);
    };
    const applyRemotePreview = (preview: TiledCameraPreviewDomain) => {
      if (gestureActiveRef.current) return;
      if (preview == null) {
        clearRemotePreview();
        return;
      }
      const { startMs, endMs, left, width: plotWidth } = followerGeomRef.current;
      const committedSpan = endMs - startMs;
      const previewSpan = preview[1] - preview[0];
      if (committedSpan <= 0 || previewSpan <= 0 || plotWidth <= 0) return;
      const scaleX = committedSpan / previewSpan;
      const translateX = left * (1 - scaleX) + ((startMs - preview[0]) * plotWidth) / previewSpan;
      plotGroupRef.current?.setAttribute("transform", `translate(${translateX},0) scale(${scaleX},1)`);
      axisRef.current?.setPreview(preview);
      gridRef.current?.setPreview(preview);
    };
    const unsubscribe = cameraSync.subscribe(syncSourceRef.current, applyRemotePreview);
    return () => {
      unsubscribe();
      // A preview applied by a departing group must not linger as a stale
      // transform if the group is swapped or removed mid-gesture.
      if (!gestureActiveRef.current) clearRemotePreview();
    };
  }, [cameraSync, gestureActiveRef]);

  useEffect(() => {
    onPlotWidthChange?.(Math.max(0, width - dynamicMargin.left - dynamicMargin.right));
  }, [width, dynamicMargin, onPlotWidthChange]);

  // Element-agnostic: the camera surface is a <rect>, the excursion marks that forward to it are
  // <line>s, and the handler only reads clientX.
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGElement>) => {
      if (gestureActiveRef.current) return;
      const { left } = svgRef.current?.getBoundingClientRect() || { left: 0 };
      const mouseX = event.clientX - left;

      if (event.timeStamp - (lastMoveRef.current || 0) < 16) return;
      lastMoveRef.current = event.timeStamp;

      setTooltipData(
        findNearestTooltipData({
          mouseX,
          datasets,
          seriesMetadata,
          seriesTypes,
          xScale,
          yScale,
          plotLeft: dynamicMargin.left,
          plotRight: width - dynamicMargin.right,
        })
      );
    },
    [datasets, seriesMetadata, seriesTypes, xScale, yScale, width, dynamicMargin, gestureActiveRef]
  );

  const handleMouseLeave = useCallback(() => setTooltipData(null), []);

  const entrance = useChartEntrance();

  const contextValue = {
    width,
    height,
    xScale,
    yScale,
    yFormatter: axisFormatter,
    animationSettings: defaultAnimationSettings,
    entrance,
    tooltip: {
      data: tooltipData,
      show: setTooltipData,
      hide: () => setTooltipData(null),
    },
    datasets,
    seriesMetadata,
    seriesTypes,
  };

  let seriesIndex = 0;
  const seriesChildren: React.ReactNode[] = [];
  flattenChartChildren(children).forEach((child) => {
    if (!isValidElement(child)) {
      seriesChildren.push(child);
      return;
    }
    if (isSeriesElement(child)) {
      const currentSeriesIndex = seriesIndex;
      seriesIndex++;
      const childProps = child.props as SeriesProps;
      seriesChildren.push(
        React.cloneElement(child, {
          ...childProps,
          ...(seriesMetadata[currentSeriesIndex] && isSeriesHidden(seriesMetadata[currentSeriesIndex])
            ? { data: [] }
            : {}),
          color: seriesMetadata[currentSeriesIndex]?.color,
          animate: false,
        } as Partial<SeriesProps>)
      );
    } else {
      seriesChildren.push(child);
    }
  });

  const ownToggle = ownsLegend && seriesMetadata.length > 1 ? toggleOwnLegendItem : undefined;
  const visibleSeriesCount = seriesMetadata.filter((meta) => !isSeriesHidden(meta)).length;
  const legendItems =
    legend ??
    inLegendOrder(seriesMetadata).map((meta) => {
      const isHidden = isSeriesHidden(meta);
      return {
        category: meta.label,
        fill: meta.color,
        isHidden,
        ...(ownToggle ? { id: meta.id } : {}),
        ...(ownToggle && !isHidden && visibleSeriesCount <= 1 ? { isDisabled: true } : {}),
      };
    });

  const handleExport = useChartExport({
    datasets,
    metadata: {
      xLabel: "Date",
      yLabel: "Value",
      seriesLabels: seriesMetadata.map((s) => s.label),
      timestamp: true,
    },
    svgRef,
  });

  const chartContent = (
    <>
      <title>{title}</title>
      <g>
        {yScale.ticks(yTickCount).map((tickValue) => {
          const isZeroLine = Math.abs(tickValue) < Number.EPSILON;
          return (
            <Line
              key={`grid-line-${tickValue}`}
              from={{ x: dynamicMargin.left, y: yScale(tickValue) }}
              to={{ x: width - dynamicMargin.right, y: yScale(tickValue) }}
              stroke={isZeroLine ? "var(--color-border-default)" : "var(--color-border-muted)"}
              strokeWidth={isZeroLine ? 1.5 : 1}
            />
          );
        })}
      </g>

      <LiveTimeGridlines
        ref={gridRef}
        scale={xScale}
        width={width - dynamicMargin.left - dynamicMargin.right}
        top={yScale.range()[1]}
        bottom={yScale.range()[0]}
      />

      <defs>
        <clipPath id={plotClipId}>
          <rect
            x={xScale.range()[0]}
            y={yScale.range()[1]}
            width={xScale.range()[1] - xScale.range()[0]}
            height={yScale.range()[0] - yScale.range()[1]}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${plotClipId})`}>
        <g data-camera-plot-group="" ref={plotGroupRef}>
          {seriesChildren}
        </g>
      </g>

      {referenceLines && referenceLines.length > 0 && (
        <g>
          {referenceLines.map((ref, index) => {
            const y = yScale(ref.value);
            const stroke = ref.color ?? "var(--color-error-base)";
            return (
              // Keyed on POSITION, not on `ref.value`: two lines at the same value — "nominal" and
              // "target", say — collide on a value key, and React drops one on re-render. Latent
              // rather than live (every caller today passes distinct values, e.g. the ANSI pair at
              // 228/252), and fixed because `ChartContainer.tsx:1080` was fixed the same way and
              // leaving one of a matched pair on a value key is how the pattern comes back.
              // biome-ignore lint/suspicious/noArrayIndexKey: position IS the identity here — the list
              // is a static per-render prop, not a reorderable collection.
              <g key={`ref-line-${index}`}>
                <Line
                  from={{ x: dynamicMargin.left, y }}
                  to={{ x: width - dynamicMargin.right, y }}
                  stroke={stroke}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                />
                {ref.label && (
                  <text x={width - dynamicMargin.right - 4} y={y - 4} textAnchor="end" fontSize={11} fill={stroke}>
                    {ref.label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      )}

      <LiveTimeAxisBottom
        ref={axisRef}
        top={height - dynamicMargin.bottom}
        scale={xScale}
        width={width - dynamicMargin.left - dynamicMargin.right}
      />
      <ChartAxis.Left
        left={dynamicMargin.left}
        scale={yScale}
        yFormatter={axisFormatter}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
      />

      {tooltipData && !tooltipData.isEvent && (
        <>
          <Line
            data-testid="chart-hover-crosshair"
            from={{ x: xScale(tooltipData.xValue), y: yScale.range()[1] }}
            to={{ x: xScale(tooltipData.xValue), y: yScale.range()[0] }}
            stroke="var(--color-text-secondary)"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.7}
          />
          {tooltipData.series
            .filter((series) => series.type !== "bar" && series.showMarker !== false)
            .map((series) => (
              <g key={`highlight-${series.label}`}>
                <circle
                  cx={xScale(tooltipData.xValue)}
                  cy={yScale(series.chartY ?? series.value)}
                  r={8}
                  fill="white"
                  stroke={series.color}
                  strokeWidth={2}
                  style={{ transition: "all 100ms ease-out" }}
                />
                <circle
                  cx={xScale(tooltipData.xValue)}
                  cy={yScale(series.chartY ?? series.value)}
                  r={4}
                  fill={series.color}
                  style={{ transition: "all 100ms ease-out" }}
                />
              </g>
            ))}
        </>
      )}

      <rect
        data-camera-surface=""
        x={dynamicMargin.left}
        y={dynamicMargin.top}
        width={width - dynamicMargin.left - dynamicMargin.right}
        height={height - dynamicMargin.top - dynamicMargin.bottom}
        fill="transparent"
        style={{ cursor: "grab", touchAction: "none" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        // macOS turns control+click into a secondary click, so a control-drag to select would open
        // the context menu mid-gesture. Suppressed only while that modifier is held — a plain
        // right-click on a chart still gets the browser's menu, as it does everywhere else.
        onContextMenu={(e) => {
          if (e.ctrlKey || e.metaKey) e.preventDefault();
        }}
        ref={wheelTargetRef}
      />

      {/* After the camera surface so the hover target is reachable; the wide transparent line is
          the hit area, since a 1px rule is not something a pointer can find. The group takes the
          gesture's live transform (see `overlayRef`) so the marks pan with the data under them. */}
      <g ref={markerOverlayRef}>
        {xMarkers?.map((marker) => {
          const x = xScale(new Date(marker.timeMs));
          if (!Number.isFinite(x) || x < dynamicMargin.left || x > width - dynamicMargin.right) return null;
          const markerColor = marker.color ?? "var(--color-error-base)";
          const showMarkerTooltip = () => {
            if (!marker.point) return;
            setTooltipData({
              xValue: new Date(marker.timeMs),
              series: [{ label: marker.point.label, value: marker.point.value, color: markerColor, showMarker: false }],
              x,
              y: yScale(marker.point.value),
              position: x > (dynamicMargin.left + (width - dynamicMargin.right)) / 2 ? "left" : "right",
            });
          };
          return (
            <g key={`${marker.timeMs}:${marker.label}`}>
              {/* No <title>: the hover tooltip below says the same thing in the chart's own box,
                  and the native one arrived late, in a second style, over the top of it. */}
              {/* The hit area sits above the camera surface, so it owns hover here. Without a
                  `point` it hands hover back to the plot rather than blanking the tooltip. */}
              <line
                x1={x}
                x2={x}
                y1={dynamicMargin.top}
                y2={height - dynamicMargin.bottom}
                stroke="transparent"
                strokeWidth={7}
                onMouseMove={marker.point ? showMarkerTooltip : handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
              <line
                x1={x}
                x2={x}
                y1={dynamicMargin.top}
                y2={height - dynamicMargin.bottom}
                stroke={markerColor}
                strokeWidth={1}
                strokeOpacity={0.75}
                // The live transform scales x during a wheel zoom; the rule should stay 1px.
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            </g>
          );
        })}
      </g>

      <SelectionBand
        ref={selectionRef}
        leftPx={dynamicMargin.left}
        topPx={dynamicMargin.top}
        widthPx={width - dynamicMargin.left - dynamicMargin.right}
        heightPx={height - dynamicMargin.top - dynamicMargin.bottom}
      />
    </>
  );

  return expand.present(
    <div className="relative flex h-full flex-col">
      <ChartContext.Provider value={contextValue}>
        <ChartHeader title={title} controls={headerControls} actions={resolvedActions} containerRef={headerRef} />

        <svg width={width} height={height} className="overflow-visible" ref={svgRef} role="img" aria-label={title}>
          {chartContent}
        </svg>

        <ChartTooltip data={tooltipData} formatter={axisFormatter} />

        <div ref={bottomBarRef}>
          <ChartBottomBar
            onExport={handleExport}
            items={legendItems}
            hideExport={hideExport}
            // A small inset, not the y-axis gutter: flush left the first swatch sits on the card
            // border, but indenting it the full axis width leaves the legend adrift from the edge
            // every other row in the card lines up against.
            marginLeft={12}
            marginRight={12}
            {...((onLegendItemToggle ?? ownToggle) && { onLegendItemToggle: onLegendItemToggle ?? ownToggle })}
          />
        </div>
      </ChartContext.Provider>
    </div>
  );
};

/**
 * TiledTimeSeriesChart
 *
 * Camera-mode time-series chart for the meter telemetry explorer. A parent-owned
 * `viewport` (ms) drives the x-scale directly — decoupled from the loaded data
 * extent — and pan/zoom gestures commit a new viewport once per settled gesture.
 * The data is assumed pre-bounded to the viewport (± overscan) upstream, so the
 * series group is wrapped in a plot-area clip and series animation is disabled.
 */
export const TiledTimeSeriesChart = withParentSize(VisXTiledTimeSeriesChart) as unknown as React.FC<
  Omit<TiledTimeSeriesChartProps, "parentWidth" | "parentHeight"> & {
    parentWidth?: number;
    parentHeight?: number;
  }
>;
